import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { signedPhotoUrl } from '../lib/photos'
import { useHubLang } from '../lib/i18n'
import { COMPOSE, CONTESTS } from '../lib/hubCopy'
import { altFor, type GalleryEntry } from '../lib/photoContests'
import { Dialog } from './Dialog'
import { ChevronIcon } from './icons'

/**
 * A contest's photos, one at a time and large, with the vote right there
 * (David, 11 Oct).
 *
 * Before this, a contest tile was a link to its POST (`/item/:id?photo=`), so
 * the viewer that opened walked that post's photos — usually one — and the
 * page around it was a single post with "Back to the hub". Trying to move
 * through the contest put you in the hub feed. The contest is the set people
 * are judging, so the contest is what this walks.
 *
 * ── THE SAME FRAME AS THE FEED'S LIGHTBOX (141) ─────────────────────────────
 *
 * A Dialog with `className="lightbox"`: focus trap, Escape, focus return and
 * the scroll lock are Dialog's, the dark full-bleed skin is the lightbox's,
 * and arrows / buttons / a sideways swipe move. It stops at the ends rather
 * than wrapping. Unlike the feed's, it walks ACROSS posts — which 141 refused
 * for the feed because a caption belongs to its post. Here every photo carries
 * its own description, and nothing shows whose it is while voting is open
 * (147), so there is no post context to lose.
 *
 * ── PAGING ──────────────────────────────────────────────────────────────────
 *
 * The gallery is paged by the server (GALLERY_PAGE). Two from the end of what
 * is loaded, the next page is asked for, so "Next" on the last loaded photo is
 * already a photo. The title counts against the contest's total, not the page.
 *
 * ── THE VOTE IS THE PAGE'S OWN CONTROL ──────────────────────────────────────
 *
 * `voteControls` is the same function that draws the tile's button, passed in,
 * so the reasons a vote is off (yours, you judge, not open, used up) cannot
 * drift between the grid and the viewer.
 */
export function ContestViewer({
  entries,
  total,
  index,
  onIndex,
  onClose,
  onNeedMore,
  meta,
  voteControls,
}: {
  entries: GalleryEntry[]
  total: number
  index: number
  onIndex: (i: number) => void
  onClose: () => void
  /** Load the next page. Called at most once per page; the caller ignores repeats. */
  onNeedMore: () => void
  /** The line under the photo: place, posted by, votes — the tile's own line. */
  meta: (e: GalleryEntry) => string | null
  voteControls: (e: GalleryEntry) => ReactNode
}) {
  const lang = useHubLang()
  const t = COMPOSE[lang]
  const c = CONTESTS[lang]
  const e = entries[index]
  const n = Math.max(total, entries.length)
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const touch = useRef<{ x: number; y: number } | null>(null)

  const hasPrev = index > 0
  const hasNext = index < entries.length - 1

  // Ask for the next page two from the end of what is loaded.
  useEffect(() => {
    if (entries.length < total && index >= entries.length - 2) onNeedMore()
  }, [index, entries.length, total, onNeedMore])

  // Keyed on the paths, not the array: a vote replaces `entries` with a new
  // array, and re-signing on that would flash the grey holder under the vote.
  const prevPath = entries[index - 1]?.path
  const nextPath = entries[index + 1]?.path
  useEffect(() => {
    if (!e) return
    let live = true
    setUrl(null)
    setFailed(false)
    signedPhotoUrl(e.path)
      .then((u) => {
        if (!live) return
        if (u) setUrl(u)
        else setFailed(true)
      })
      .catch(() => live && setFailed(true))
    // Look-ahead: a swipe on a site connection should not be a grey box.
    for (const near of [nextPath, prevPath]) {
      if (!near) continue
      signedPhotoUrl(near)
        .then((u) => {
          if (u) new Image().src = u
        })
        .catch(() => {})
    }
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e?.path, nextPath, prevPath])

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'ArrowLeft' && hasPrev) {
        ev.preventDefault()
        onIndex(index - 1)
      } else if (ev.key === 'ArrowRight' && hasNext) {
        ev.preventDefault()
        onIndex(index + 1)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [index, hasPrev, hasNext, onIndex])

  if (!e) return null

  function onTouchStart(ev: React.TouchEvent) {
    const p = ev.touches[0]
    touch.current = p ? { x: p.clientX, y: p.clientY } : null
  }
  function onTouchEnd(ev: React.TouchEvent) {
    const start = touch.current
    touch.current = null
    const p = ev.changedTouches[0]
    if (!start || !p) return
    const dx = p.clientX - start.x
    const dy = p.clientY - start.y
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return
    if (dx < 0 && hasNext) onIndex(index + 1)
    if (dx > 0 && hasPrev) onIndex(index - 1)
  }

  const caption = altFor(e, lang)
  const line = meta(e)

  return (
    <Dialog open title={n > 1 ? t.photoOf(index + 1, n) : t.photo} onClose={onClose} className="lightbox contestviewer">
      <figure className="lightbox__figure">
        <div className="lightbox__stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {failed ? (
            <p className="lightbox__failed">{t.photoFailed}</p>
          ) : url ? (
            <img
              key={e.entryId}
              className="lightbox__img"
              src={url}
              alt={caption ? '' : t.photoOf(index + 1, n)}
              width={e.width ?? undefined}
              height={e.height ?? undefined}
              onError={() => setFailed(true)}
            />
          ) : (
            <div
              className="lightbox__holder"
              style={e.width && e.height ? { aspectRatio: `${e.width} / ${e.height}` } : undefined}
              aria-hidden="true"
            />
          )}
          {n > 1 && (
            <>
              <button
                type="button"
                className="lightbox__nav lightbox__nav--prev"
                onClick={() => onIndex(index - 1)}
                disabled={!hasPrev}
                aria-label={t.previousPhoto}
              >
                <ChevronIcon size={28} />
              </button>
              <button
                type="button"
                className="lightbox__nav lightbox__nav--next"
                onClick={() => onIndex(index + 1)}
                disabled={!hasNext}
                aria-label={t.nextPhoto}
              >
                <ChevronIcon size={28} />
              </button>
            </>
          )}
        </div>
        {caption && <figcaption className="lightbox__caption">{caption}</figcaption>}
        {line && <p className="contestviewer__meta">{line}</p>}
        <div className="contestviewer__actions">
          {voteControls(e)}
          {e.contentId && (
            <Link className="btn btn--link btn--sm contestviewer__post" to={`/item/${e.contentId}?photo=${encodeURIComponent(e.path)}`}>
              {c.openPost}
            </Link>
          )}
        </div>
      </figure>
    </Dialog>
  )
}
